"use client";

import { Calendar, ChevronLeft, Shield, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="h-12 w-3/4 rounded-md bg-muted/40 animate-pulse" />
        <div className="h-96 w-full rounded-lg bg-muted/40 animate-pulse" />
        <div className="space-y-3">
          <div className="h-4 w-full rounded bg-muted/40 animate-pulse" />
          <div className="h-4 w-5/6 rounded bg-muted/40 animate-pulse" />
          <div className="h-4 w-4/6 rounded bg-muted/40 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-gray-500">Failed to load article.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-gray-500">No article found.</p>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <div className="mb-8">
        <Link
          to="/HomePage"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Home
        </Link>
      </div>

      {}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1 text-xs font-medium text-black">
          <Shield className="h-3 w-3" />
          {post.categories && post.categories.length > 0
            ? post.categories[0].name
            : "Luxury Travel"}
        </span>
        {post.categories &&
          post.categories.slice(1, 3).map((cat, i) => (
            <span
              key={cat.id}
              data-index={i}
              className="inline-flex items-center rounded-lg bg-gray-200 px-3 py-1 text-xs font-medium text-gray-800"
            >
              {cat.name}
            </span>
          ))}
      </div>

      {}
      <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-black mb-6 leading-tight">
        {post.title}
      </h1>

      {}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-gray-600 mb-10">
        {post.author && (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-black" />
            <span>{post.author.name}</span>
          </div>
        )}
        {formattedDate && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-black" />
            <span>{formattedDate}</span>
          </div>
        )}
      </div>

      {}
      {post.featuredImage && (
        <div className="relative mb-12 overflow-hidden rounded-lg border border-gray-200 shadow-md">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover"
          />
          <div className="absolute inset-0 ring-1 ring-inset ring-black/10 rounded-lg pointer-events-none" />
        </div>
      )}

      {}
      {post.content && (
        <div
          className="prose prose-black max-w-none font-default text-base md:text-lg leading-relaxed text-gray-800 [&>h2]:font-default [&>h2]:text-2xl [&>h2]:md:text-3xl [&>h2]:font-bold [&>h2]:mt-12 [&>h2]:mb-4 [&>h2]:text-black [&>h3]:font-default [&>h3]:text-xl [&>h3]:md:text-2xl [&>h3]:font-semibold [&>h3]:mt-8 [&>h3]:mb-3 [&>h3]:text-black [&>p]:mb-6 [&>p]:text-gray-700 [&>ul]:mb-6 [&>ul]:pl-6 [&>ul>li]:list-disc [&>ul>li]:mb-2 [&>ol]:mb-6 [&>ol]:pl-6 [&>ol>li]:list-decimal [&>ol>li]:mb-2 [&>blockquote]:border-l-4 [&>blockquote]:border-black [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-gray-500 [&>blockquote]:my-8 [&>a]:text-black [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-gray-700 [&>code]:font-mono [&>code]:bg-gray-100 [&>code]:px-1.5 [&>code]:py-0.5 [&>code]:rounded [&>code]:text-sm [&>pre]:bg-gray-900 [&>pre]:border [&>pre]:border-gray-800 [&>pre]:rounded-lg [&>pre]:p-4 [&>pre]:overflow-x-auto [&>pre]:my-6 [&_img]:rounded-lg [&_img]:my-6"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500 mr-2">Tags:</span>
          {post.tags.map((tag, i) => (
            <span
              key={tag.id}
              data-index={i}
              className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:border-black transition-colors"
            >
              #{tag.name}
            </span>
          ))}
        </div>
      )}

      <Separator className="my-12" />

      {}
      {post.author && (
        <Card className="bg-gray-50 border border-gray-200">
          <CardContent className="flex flex-col sm:flex-row items-start gap-6 p-6">
            <Avatar className="h-20 w-20 ring-2 ring-black/10">
              {post.author.avatar && (
                <AvatarImage
                  src={post.author.avatar["96"] || post.author.avatar["48"]}
                  alt={post.author.name}
                />
              )}
              <AvatarFallback className="bg-gray-200 text-black text-lg font-semibold">
                {post.author.name?.charAt(0) || "B"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">
                Author
              </p>
              <h3 className="font-default text-xl font-semibold text-black mb-2">
                {post.author.name}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Senior Chauffeur and Fleet Manager at BEMAX LIMO CHAUFFEURED SERVICE, dedicated to providing unparalleled luxury transportation experiences.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {}
      <div className="mt-12 rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-8 md:p-10 text-center shadow-md">
        <Shield className="h-10 w-10 text-black mx-auto mb-4" />
        <h3 className="font-default text-2xl md:text-3xl font-bold text-black mb-3">
          Experience true Luxury
        </h3>
        <p className="text-gray-600 mb-6 max-w-xl mx-auto">
          Secure your private chauffeur today and enjoy the comfort and reliability of BEMAX LIMO CHAUFFEURED SERVICE.
        </p>
        <Link to="https://book.mylimobiz.com/v4/bemax" newTab>
          <Button size="lg" className="font-medium bg-black text-white hover:bg-gray-800 rounded-lg">
            Reserve Your Ride
          </Button>
        </Link>
      </div>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            data-index={i}
            className="h-64 rounded-lg bg-gray-100 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.slice(0, 3).map((post, i) => (
        <Link
          key={post.id}
          to={post.link || "#"}
          data-index={i}
          className="group block"
        >
          <Card className="h-full bg-white border border-gray-200 hover:border-black transition-all duration-300 hover:shadow-lg overflow-hidden rounded-lg">
            {post.featuredImage && (
              <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            )}
            <CardContent className="p-6">
              {post.categories && post.categories.length > 0 && (
                <p className="text-xs font-medium uppercase tracking-widest text-gray-500 mb-2">
                  {post.categories[0].name}
                </p>
              )}
              <h4 className="font-default text-lg font-semibold text-black leading-snug mb-2 group-hover:text-gray-800 transition-colors line-clamp-2">
                {post.title}
              </h4>
              {post.excerpt && (
                <p
                  className="text-sm text-gray-600 line-clamp-2"
                  dangerouslySetInnerHTML={{
                    __html: post.excerpt.replace(/<[^>]*>/g, ""),
                  }}
                />
              )}
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

export default function Post() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="335"
      id="single-post-template-section"
      className="bg-white text-black py-16 md:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
    >
      {}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(black 1px, transparent 1px), linear-gradient(90deg, black 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        <div className="max-w-6xl mx-auto mt-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs font-medium uppercase tracking-widest text-gray-500 mb-2">
                Explore More
              </p>
              <h2 className="font-default text-3xl md:text-4xl font-bold text-black">
                Related Articles
              </h2>
            </div>
          </div>
          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}